import React, { useCallback, useEffect, useState } from 'react';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';
import { formatDateTime12hIST } from '../utils/dateDisplay';
import { AppHeader, EmptyState, SectionCard, StatusBanner } from '../components/ui';

function TransactionReportPage() {
  const [period, setPeriod] = useState('month');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [report, setReport] = useState({ transactions: [], totalCount: 0, totalAmount: 0 });
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (selectedPeriod, selectedPage = 1, signal) => {
    const axiosOpts = signal ? { signal } : {};
    try {
      const data = await webApi.getTransactionReport(
        {
          period: selectedPeriod,
          limit: pageSize,
          offset: (selectedPage - 1) * pageSize
        },
        axiosOpts
      );
      if (signal?.aborted) return;
      setReport(data || { transactions: [], totalCount: 0, totalAmount: 0 });
      setError('');
    } catch (err) {
      if (signal?.aborted || isCanceledRequest(err)) return;
      setError(err.message || 'Failed to load report');
    }
  }, [pageSize]);

  useEffect(() => {
    const ac = new AbortController();
    load(period, page, ac.signal);
    return () => ac.abort();
  }, [period, page, load]);

  const exportCsv = async () => {
    try {
      setExporting(true);
      const blob = await webApi.downloadTransactionReportCsv(period);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transaction-report-${period}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((report.totalCount || 0) / pageSize));

  return (
    <div className="page">
      <AppHeader
        title="Transaction Report"
        subtitle="Filter by period, export CSV, and paginate through records."
      />
      <StatusBanner kind="error" message={error} />

      <SectionCard>
        <div className="row">
          <select
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setPage(1);
            }}
          >
            <option value="today">Today</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
          <button type="button" className="btn btn-secondary" onClick={() => load(period, page)}>Apply Filter</button>
          <button type="button" className="btn btn-primary" onClick={exportCsv} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
        <p className="muted">
          Total Count: {report.totalCount} | Total Amount: Rs {Number(report.totalAmount || 0).toLocaleString('en-IN')}
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Employee</th>
                <th>Amount</th>
                <th>Customer</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {report.transactions.length === 0 ? (
                <tr>
                  <td colSpan="5"><EmptyState message="No report transactions found." /></td>
                </tr>
              ) : (
                report.transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>{tx.id}</td>
                    <td>{tx.employeeName || '-'}</td>
                    <td>Rs {Number(tx.amount || 0).toLocaleString('en-IN')}</td>
                    <td>{tx.customerName || '-'}</td>
                    <td>{formatDateTime12hIST(tx.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="muted">Page {page} of {totalPages}</span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </SectionCard>
    </div>
  );
}

export default TransactionReportPage;
